/**
 * Cron-endpoint voor de dagelijkse paginascan.
 *
 * Bedoeld voor een pg_cron-job (1x per dag). Beveiligd met het secret uit
 * `public.cron_secrets` (naam 'wpc-cron'), zodat zowel de app als pg_cron
 * dezelfde waarde gebruiken zonder hardcoded waarden.
 *   GET /api/public/page-audit?strategy=mobile
 */
import { createFileRoute } from "@tanstack/react-router";
import { runPageAudit } from "@/lib/page-audit.server";
import { getCronSecret } from "@/lib/cron-secrets.server";
import { AUDIT_STRATEGIES, type AuditStrategy } from "@/lib/page-audit";

async function run(request: Request) {
  const secret = await getCronSecret("wpc-cron");
  if (secret) {
    const provided =
      request.headers.get("x-cron-secret") ??
      request.headers.get("authorization")?.replace("Bearer ", "") ??
      "";
    if (provided !== secret) return new Response("Unauthorized", { status: 401 });
  }

  const param = new URL(request.url).searchParams.get("strategy");
  const strategy: AuditStrategy = (AUDIT_STRATEGIES as readonly string[]).includes(param ?? "")
    ? (param as AuditStrategy)
    : "mobile";

  try {
    // Zonder geconfigureerd cron-secret is dit endpoint publiek: een throttle
    // voorkomt dat iemand herhaaldelijk scans (en dus PSI-quota) kan opstoken.
    if (!secret) {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const since = new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString();
      const { data: recent } = await supabaseAdmin
        .from("page_audit_runs")
        .select("id")
        .gte("started_at", since)
        .limit(1);
      if ((recent ?? []).length > 0) {
        return Response.json({ ok: false, error: "recent al gescand" }, { status: 429 });
      }
    }

    const result = await runPageAudit(strategy, "cron");
    return Response.json(result);
  } catch (error) {
    console.error("[page-audit]", (error as Error).message);
    return Response.json({ ok: false, error: "scan mislukt" }, { status: 500 });
  }
}

export const Route = createFileRoute("/api/public/page-audit")({
  staticData: { sitemap: false },
  server: {
    handlers: {
      GET: async ({ request }) => run(request),
      POST: async ({ request }) => run(request),
    },
  },
});
